import {
  academyAreaContent,
  academySkillContent,
} from './academyContent';
import type {
  AcademyArea,
  AcademyLesson,
  AcademyMetric,
  AcademyPlanState,
  AcademySkillPath,
  AcademyTier,
  TeacherAcademyDashboard,
  TeacherAcademyDashboardResponse,
} from '../AcademyService';
import {
  getBestAcademyCompletedAt,
  getBestAcademyConfirmedBy,
} from '../../../../components/academy/Utilities/academyProgress';
import type { AcademyAreaContent, AcademySkillContent, AcademyTierContent } from './types';

type AcademySkillState = AcademyPlanState['tiers'][number]['skills'][number];
type AcademyMetricValue = NonNullable<AcademyPlanState['metricValues']>[number];
type AcademyMetricValues = AcademyMetricValue[];

export function buildTeacherAcademyDashboard(
  response: TeacherAcademyDashboardResponse,
): TeacherAcademyDashboard {
  return createAcademyDashboardBuilder()(response);
}

export function createAcademyDashboardBuilder(
  areas: AcademyAreaContent[] = academyAreaContent,
  skills: AcademySkillContent[] = academySkillContent,
): (response: TeacherAcademyDashboardResponse) => TeacherAcademyDashboard {
  const skillContentByKey = new Map(skills.map((skill) => [skill.skillKey, skill]));

  return (response) => {
    const plans = mergePlansByToken(getResponsePlans(response));

    if (shouldAssertApiSkillsHaveLocalContent()) {
      assertApiContentHasLocalContent(plans, areas, skillContentByKey);
    }

    const planByToken = new Map(plans.map((plan) => [plan.planToken, plan]));
    const academyAreas = areas
      .map((area) => buildAcademyArea(area, planByToken.get(area.planToken), skillContentByKey))
      .filter((area): area is AcademyArea => Boolean(area));

    return {
      hasAcademyPlans: plans.length > 0,
      defaultAcademyAreaKey: academyAreas[0]?.academyAreaKey || '',
      academyAreas,
    };
  };
}

function buildAcademyArea(
  area: AcademyAreaContent,
  plan: AcademyPlanState | undefined,
  skillContentByKey: Map<string, AcademySkillContent>,
): AcademyArea | null {
  if (!plan) {
    return null;
  }

  const apiTiers = getPlanTiers(plan);
  const localTierContentByKey = new Map(area.tiers.map((tier) => [tier.tierKey, tier]));
  const planMetrics = buildMetrics(area.metrics, plan.metricValues);
  const tiers = apiTiers
    .map((tier, index) => buildTier(tier, localTierContentByKey.get(tier.tierKey), planMetrics, index))
    .filter((tier): tier is AcademyTier => Boolean(tier));
  const visibleTierKeys = new Set(tiers.map((tier) => tier.tierKey));
  const lessonsByPath = getLessonsByPath(apiTiers, visibleTierKeys, skillContentByKey);
  const skillPaths = area.skillPaths
    .map((skillPath): AcademySkillPath => ({
      id: skillPath.pathKey,
      pathKey: skillPath.pathKey,
      title: skillPath.title,
      displayOrder: skillPath.displayOrder,
      skills: getPathLessons(lessonsByPath, skillPath.pathKey),
    }))
    .sort((first, second) => first.displayOrder - second.displayOrder);

  if (!skillPaths.some((path) => path.skills.length > 0)) {
    return null;
  }

  return {
    academyAreaKey: area.academyAreaKey,
    planToken: plan.planToken,
    planName: plan.planName,
    label: area.label,
    heading: area.heading,
    description: area.description,
    metrics: planMetrics,
    tiers,
    recommendedLessons: [],
    skillPaths,
  };
}

function buildTier(
  tier: AcademyPlanState['tiers'][number],
  tierContent: AcademyTierContent | undefined,
  metrics: AcademyMetric[],
  index: number,
): AcademyTier | null {
  if (!tierContent) {
    return null;
  }

  return {
    tierKey: tier.tierKey,
    nextTierKey: tier.nextTierKey,
    step: index + 1,
    name: tierContent.name,
    description: tierContent.description,
    metrics,
  };
}

function buildLesson(
  state: AcademySkillState,
  tierKey: string,
  skillContentByKey: Map<string, AcademySkillContent>,
): AcademyLesson | null {
  const content = skillContentByKey.get(state.skillKey);

  if (!content) {
    return null;
  }

  return {
    id: state.skillKey,
    skillKey: state.skillKey,
    title: content.title,
    description: content.description,
    pathKey: state.skillPath,
    learnType: content.learnType,
    tierKeys: [tierKey],
    displayOrder: state.displayOrder,
    recommendedOrder: state.recommendedOrder,
    learnConfirmedBy: state.learnConfirmedBy,
    applyConfirmedBy: state.applyConfirmedBy,
    learnCompletedAt: state.learnCompletedAt,
    applyCompletedAt: state.applyCompletedAt,
    learnContent: content.learnContent,
    learnRequired: state.learnRequired,
    applyRequired: state.applyRequired,
    confirmationPolicy: content.confirmationPolicy,
    appliedActions: content.appliedActions,
    selfReportPrompt: content.selfReportPrompt,
  };
}

function assertApiContentHasLocalContent(
  plans: AcademyPlanState[],
  areas: AcademyAreaContent[],
  skillContentByKey: Map<string, AcademySkillContent>,
): void {
  const missingSkillKeys = plans
    .flatMap((plan) => getPlanTiers(plan).flatMap((tier) => getTierSkills(tier).map((skill) => skill.skillKey)))
    .filter((skillKey) => !skillContentByKey.has(skillKey));
  const planByToken = new Map(plans.map((plan) => [plan.planToken, plan]));
  const missingTierLabels = areas.flatMap((area) => {
    const plan = planByToken.get(area.planToken);
    const localTierKeys = new Set(area.tiers.map((tier) => tier.tierKey));

    return (plan ? getPlanTiers(plan) : [])
      .map((tier) => tier.tierKey)
      .filter((tierKey) => !localTierKeys.has(tierKey))
      .map((tierKey) => `${area.academyAreaKey}:${tierKey}`);
  });

  if (missingSkillKeys.length > 0) {
    throw new Error(
      `Academy content is missing for skills: ${Array.from(new Set(missingSkillKeys)).join(', ')}`,
    );
  }

  if (missingTierLabels.length > 0) {
    throw new Error(
      `Academy content is missing for tiers: ${Array.from(new Set(missingTierLabels)).join(', ')}`,
    );
  }
}

function shouldAssertApiSkillsHaveLocalContent(): boolean {
  return process.env.NODE_ENV !== 'production';
}

function buildMetrics(
  metrics: Array<{ metricKey: string; label: string }>,
  metricValues: AcademyPlanState['metricValues'],
): AcademyMetric[] {
  const metricValueByKey = new Map(
    getMetricValues(metricValues).map((metricValue) => [metricValue.metricKey, metricValue.value]),
  );

  return metrics.map((metric) => ({
    metricKey: metric.metricKey,
    label: metric.label,
    value: metricValueByKey.get(metric.metricKey) || 0,
  }));
}

function getLessonsByPath(
  tiers: AcademyPlanState['tiers'],
  visibleTierKeys: Set<string>,
  skillContentByKey: Map<string, AcademySkillContent>,
): Map<AcademySkillState['skillPath'], AcademyLesson[]> {
  const lessonByPlacement = new Map<string, AcademyLesson>();

  tiers.forEach((tier) => {
    if (!visibleTierKeys.has(tier.tierKey)) {
      return;
    }

    getTierSkills(tier).forEach((state) => {
      const lesson = buildLesson(state, tier.tierKey, skillContentByKey);

      if (!lesson) {
        return;
      }

      const placementKey = `${lesson.pathKey}:${lesson.skillKey}`;
      const existingLesson = lessonByPlacement.get(placementKey);

      lessonByPlacement.set(placementKey, existingLesson ? mergeLessonPlacement(existingLesson, lesson) : lesson);
    });
  });

  const lessonsByPath = new Map<AcademySkillState['skillPath'], AcademyLesson[]>();

  lessonByPlacement.forEach((lesson) => {
    const pathLessons = lessonsByPath.get(lesson.pathKey) || [];
    pathLessons.push(lesson);
    lessonsByPath.set(lesson.pathKey, pathLessons);
  });

  lessonsByPath.forEach((lessons) => lessons.sort(compareLessonsByDisplayOrder));

  return lessonsByPath;
}

function getPathLessons(
  lessonsByPath: Map<AcademySkillState['skillPath'], AcademyLesson[]>,
  pathKey: AcademySkillState['skillPath'],
): AcademyLesson[] {
  return lessonsByPath.get(pathKey) || [];
}

function mergeLessonPlacement(currentLesson: AcademyLesson, nextLesson: AcademyLesson): AcademyLesson {
  const learnConfirmedBy = getBestAcademyConfirmedBy(currentLesson.learnConfirmedBy, nextLesson.learnConfirmedBy);
  const applyConfirmedBy = getBestAcademyConfirmedBy(currentLesson.applyConfirmedBy, nextLesson.applyConfirmedBy);

  return {
    ...currentLesson,
    tierKeys: Array.from(new Set([...currentLesson.tierKeys, ...nextLesson.tierKeys])),
    displayOrder: Math.min(currentLesson.displayOrder, nextLesson.displayOrder),
    recommendedOrder: Math.min(currentLesson.recommendedOrder, nextLesson.recommendedOrder),
    learnRequired: currentLesson.learnRequired || nextLesson.learnRequired,
    applyRequired: currentLesson.applyRequired || nextLesson.applyRequired,
    learnConfirmedBy,
    applyConfirmedBy,
    learnCompletedAt: getBestAcademyCompletedAt(
      learnConfirmedBy,
      currentLesson.learnConfirmedBy,
      currentLesson.learnCompletedAt,
      nextLesson.learnCompletedAt,
    ),
    applyCompletedAt: getBestAcademyCompletedAt(
      applyConfirmedBy,
      currentLesson.applyConfirmedBy,
      currentLesson.applyCompletedAt,
      nextLesson.applyCompletedAt,
    ),
  };
}

function compareLessonsByDisplayOrder(first: AcademyLesson, second: AcademyLesson): number {
  return first.displayOrder - second.displayOrder || first.skillKey.localeCompare(second.skillKey);
}

function getResponsePlans(response: TeacherAcademyDashboardResponse): AcademyPlanState[] {
  return Array.isArray(response.plans) ? response.plans : [];
}

function mergePlansByToken(plans: AcademyPlanState[]): AcademyPlanState[] {
  const planByToken = new Map<string, AcademyPlanState>();

  plans.forEach((plan) => {
    const existingPlan = planByToken.get(plan.planToken);

    if (!existingPlan) {
      planByToken.set(plan.planToken, {
        ...plan,
        metricValues: getMetricValues(plan.metricValues),
        tiers: [...getPlanTiers(plan)],
      });
      return;
    }

    existingPlan.metricValues = mergeMetricValues(existingPlan.metricValues, getMetricValues(plan.metricValues));
    existingPlan.tiers = mergePlanTiers(existingPlan.tiers, getPlanTiers(plan));
  });

  return Array.from(planByToken.values());
}

function mergePlanTiers(
  currentTiers: AcademyPlanState['tiers'],
  nextTiers: AcademyPlanState['tiers'],
): AcademyPlanState['tiers'] {
  const tierByKey = new Map(currentTiers.map((tier) => [tier.tierKey, tier]));

  nextTiers.forEach((tier) => {
    const existingTier = tierByKey.get(tier.tierKey);

    if (!existingTier) {
      tierByKey.set(tier.tierKey, tier);
      return;
    }

    existingTier.skills = mergeTierSkills(existingTier.skills, getTierSkills(tier));
  });

  return Array.from(tierByKey.values());
}

function mergeMetricValues(
  currentMetrics: AcademyMetricValues,
  nextMetrics: AcademyMetricValues,
): AcademyMetricValues {
  const metricValueByKey = new Map(currentMetrics.map((metric) => [metric.metricKey, metric]));

  nextMetrics.forEach((metric) => {
    metricValueByKey.set(metric.metricKey, metric);
  });

  return Array.from(metricValueByKey.values());
}

function mergeTierSkills(
  currentSkills: AcademyPlanState['tiers'][number]['skills'],
  nextSkills: AcademyPlanState['tiers'][number]['skills'],
): AcademyPlanState['tiers'][number]['skills'] {
  const skillByKey = new Map(currentSkills.map((skill) => [skill.skillKey, skill]));

  nextSkills.forEach((skill) => {
    const currentSkill = skillByKey.get(skill.skillKey);
    skillByKey.set(skill.skillKey, currentSkill ? mergeSkillState(currentSkill, skill) : skill);
  });

  return Array.from(skillByKey.values());
}

function mergeSkillState(currentSkill: AcademySkillState, nextSkill: AcademySkillState): AcademySkillState {
  const learnConfirmedBy = getBestAcademyConfirmedBy(currentSkill.learnConfirmedBy, nextSkill.learnConfirmedBy);
  const applyConfirmedBy = getBestAcademyConfirmedBy(currentSkill.applyConfirmedBy, nextSkill.applyConfirmedBy);

  return {
    ...currentSkill,
    learnRequired: currentSkill.learnRequired || nextSkill.learnRequired,
    applyRequired: currentSkill.applyRequired || nextSkill.applyRequired,
    learnConfirmedBy,
    applyConfirmedBy,
    learnCompletedAt: getBestAcademyCompletedAt(
      learnConfirmedBy,
      currentSkill.learnConfirmedBy,
      currentSkill.learnCompletedAt,
      nextSkill.learnCompletedAt,
    ),
    applyCompletedAt: getBestAcademyCompletedAt(
      applyConfirmedBy,
      currentSkill.applyConfirmedBy,
      currentSkill.applyCompletedAt,
      nextSkill.applyCompletedAt,
    ),
  };
}

function getPlanTiers(plan: AcademyPlanState): AcademyPlanState['tiers'] {
  return Array.isArray(plan.tiers) ? plan.tiers : [];
}

function getTierSkills(tier: AcademyPlanState['tiers'][number]): AcademyPlanState['tiers'][number]['skills'] {
  return Array.isArray(tier.skills) ? tier.skills : [];
}

function getMetricValues(
  metricValues: AcademyPlanState['metricValues'],
): AcademyMetricValues {
  return Array.isArray(metricValues) ? metricValues : [];
}
